import { NavLink, useLocation } from 'react-router-dom';
import { useState, useCallback, useEffect } from 'react';
import { NAV_GROUPS, SAFE_MODE_HIDDEN_IDS, HIDE_WIP_HIDDEN_GROUPS, TEMP_HIDDEN_NAV_IDS, type NavItem } from '@features/_navigation';
import { Can } from '@services/auth';
import { Icon } from '@shared/icons';
import { cn } from '@shared/lib/cn';
import { env } from '@config/env';
import styles from './Sidebar.module.css';

interface SidebarLinkProps {
  readonly item: NavItem;
  readonly child?: boolean;
}

function SidebarLink({ item, child = false }: SidebarLinkProps): JSX.Element {
  const link = (
    <NavLink
      to={`/${item.path ?? item.id}`}
      // Parents with children use exact matching so only the deepest
      // active link claims aria-current="page".
      end={item.children !== undefined}
      className={({ isActive }) =>
        cn(styles.item, child && styles.subItem, isActive && styles.itemActive)
      }
    >
      <span className={styles.icon}>
        <Icon name={item.icon} size={14} />
      </span>
      <span className={styles.label}>{item.label}</span>
      {item.tag !== undefined ? <span className={styles.tag}>{item.tag}</span> : null}
      {item.count !== undefined ? <span className={styles.count}>{item.count}</span> : null}
    </NavLink>
  );
  return item.permission !== undefined ? <Can permission={item.permission}>{link}</Can> : link;
}

function isChildActive(item: NavItem, pathname: string): boolean {
  if (item.children === undefined) return false;
  return item.children.some((c) => pathname.startsWith(`/${c.path ?? c.id}`));
}

interface ExpandableItemProps {
  readonly item: NavItem;
  /** True when this expandable item is itself nested under another parent. */
  readonly child?: boolean;
}

function ExpandableItem({ item, child = false }: ExpandableItemProps): JSX.Element {
  const { pathname } = useLocation();
  const childActive = isChildActive(item, pathname);
  const [expanded, setExpanded] = useState(childActive);

  const toggle = useCallback(() => {
    setExpanded((s) => !s);
  }, []);

  return (
    <li key={item.id}>
      <div className={styles.itemRow}>
        <SidebarLink item={item} child={child} />
        <button
          type="button"
          className={cn(styles.expandBtn, expanded && styles.expandBtnOpen)}
          onClick={toggle}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          aria-expanded={expanded}
        >
          <Icon name="chevronRight" size={12} />
        </button>
      </div>
      {item.children !== undefined ? (
        <ul className={cn(styles.subList, !expanded && styles.subListHidden)}>
          {item.children.map((c) =>
            // A child with its own children renders as a nested expandable group so the
            // tree can go deeper than one level (e.g. API Integration → vTerminal/Paylinks).
            c.children !== undefined ? (
              <ExpandableItem key={c.id} item={c} child />
            ) : (
              <li key={c.id}>
                <SidebarLink item={c} child />
              </li>
            ),
          )}
        </ul>
      ) : null}
    </li>
  );
}

interface SidebarProps {
  /** Mobile drawer open state (no effect at ≥900px, where the sidebar is static). */
  readonly open?: boolean;
  readonly onClose?: () => void;
}

export function Sidebar({ open = false, onClose }: SidebarProps): JSX.Element {
  // Esc closes the mobile drawer.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  return (
    <>
      {open ? (
        <button
          type="button"
          className={styles.backdrop}
          aria-label="Close navigation"
          onClick={onClose}
        />
      ) : null}
      <aside className={cn(styles.sidebar, open && styles.sidebarOpen)}>
        <p className={styles.platform}>Operate as the acquirer.</p>
      <nav aria-label="Primary">
        {NAV_GROUPS
          .filter((group) => !env.VITE_HIDE_WIP || !HIDE_WIP_HIDDEN_GROUPS.has(group.label))
          .map((group, gi) => {
          const labelId = `nav-group-${String(gi)}`;
          return (
            <div key={group.label} className={styles.group}>
              <h2 id={labelId} className={styles.groupLabel}>
                {group.label}
              </h2>
              <ul aria-labelledby={labelId} className={styles.list}>
                {group.items
                  // TEMP escape hatch: unconditionally hide stop-gapped items (set is empty today).
                  .filter((item) => !TEMP_HIDDEN_NAV_IDS.has(item.id))
                  .filter((item) => !env.VITE_INTELLIGENCE_SAFE_MODE || !SAFE_MODE_HIDDEN_IDS.has(item.id))
                  .map((item) =>
                  item.children !== undefined ? (
                    <ExpandableItem key={item.id} item={item} />
                  ) : (
                    <li key={item.id}>
                      <SidebarLink item={item} />
                    </li>
                  ),
                )}
              </ul>
            </div>
          );
        })}
      </nav>
      <footer className={styles.foot}>
        <div className={styles.footRow}>
          <span>v0.1.0</span>
          <span className={styles.connected}>● connected</span>
        </div>
        <div className={styles.footRow}>
          <span>eu-west-2</span>
          <span className={styles.muted}>region</span>
        </div>
      </footer>
      </aside>
    </>
  );
}
